import SharedPreferences from "../../constants/SharedPreferences";
import { mapValidationSecretDataRequestForReauthentication } from "../../util/SecurityQuestionsValidationUtil";
import {
  getErrorMessage,
  saveInStorage,
  getFromStorage,
  convertBase64StringToArrayBuffer,
  convertArrayBufferToString,
  joinPrefixedShares,
  formatRecoveredAsPemPrivateKey,
  logApiResponse,
  saveInInternalStorage,
  getFromInternalStorage,
  getEncryptionCurveFromStorage,
} from "../../util/Utility";
import {
  getTwoFARestClient,
  getNonAuthRestClientForAdmin,
} from "../RestUtility";
import { aeadDecryptWithPasscode } from "../../crypto/AuthenticatedEncryption";
import BayunError from "../../constants/BayunError";
import Constants from "../../constants/Constants";
import { AuthenticateResponse } from "../../BayunCore";
import { decryptAsymmetric } from "../../util/AsymmetricEncryption";
import {
  getMessageFromSignatureString,
  retrieveAndverifyLastSignature,
} from "../../util/SignatureUtil";
import { derivePbkdf2Encoded, encodeKey } from "../../crypto/PBKDF2";
import { asRawKey, hmacSha256Raw } from "../../crypto/KeyMaterial";
import {
  initializeVishwamSDK,
  faceCaptureRegisterAndLogin,
} from "../VishwamApiManager";
import { getRefreshToken } from "./internal/refreshToken";
import {
  getAuthSaltForQuestion,
  findKeyAnswerSalt,
  findEncryptedUserPrivateKeyPart,
} from "./internal/securityQuestionHelpers";
import { saveOrgAuthResponseForSwitching } from "./authApi";
import { createMemberAppForSSO, createMemberData } from "./provisioningApi";
import { getMemberClientData } from "./memberApi";
import { restoreUserSigningKeys } from "../../util/UserSigningKeys";
export async function getAuthAnswerHashes(sessionId, answers) {
  // convert all answers to lower case
  answers.forEach((element) => {
    element.answer = element.answer.trim().toLowerCase();
  });

  // create authAnswerHash for all answers
  const authAnswerSalts = JSON.parse(
    await getFromStorage(sessionId, SharedPreferences.ANSWER_AUTH_SALT),
  );

  const authAnswerHashes = await Promise.all(
    answers.map(async (element) => {
      const authHash = await derivePbkdf2Encoded(
        element.answer,
        getAuthSaltForQuestion(authAnswerSalts, element.questionId),
      );

      return {
        questionId: element.questionId,
        authAnswerHash: authHash,
      };
    }),
  );

  return authAnswerHashes;
}
/**
 * This method verifies the user against the provided answers to security questions.
 *
 * @param {String} sessionId id of the session in question
 * @param {String} answers a list of all the answers corresponding to the questionIds.
 * @param {String} passcode passcode of the authenticating member.
 * @param {Function} successCallback callback is called when authentication is successful.
 * @param {Function} failureCallback callback is called if any error occurs in the authentication.
 */
export const validateSecurityQuestionsAPI = async (
  sessionId,
  answers,
  // passcode,
  authorizeMemberCallback,
  successCallback,
  failureCallback,
) => {
  // convert all answers to lower case
  answers.forEach((element) => {
    element.answer = element.answer.trim().toLowerCase();
  });

  try {
    if (
      !(await getFromStorage(sessionId, SharedPreferences.IS_NEW_AUTHENTICATION))
    ) {
      const minimumCorrectQuestions =
        Number(
          await getFromStorage(
            sessionId,
            SharedPreferences.MINIMUM_CORRECT_QUESTIONS,
          ),
        ) || 3;
      // for reauthentication, we try to reassemble the userPrivateKey with saved salts
      await decryptUserPrivateKeyForReauthentication(
        sessionId,
        JSON.parse(
          await getFromStorage(
            sessionId,
            SharedPreferences.ENCRYPTED_USER_PRIVATE_KEY_PARTS,
          ),
        ),
        JSON.parse(
          await getFromStorage(sessionId, SharedPreferences.ANSWER_KEY_SALTS),
        ),
        answers,
        minimumCorrectQuestions,
      );

      successCallback({
        sessionId,
        securityQuestions: null,
        authenticationResponse: AuthenticateResponse.SUCCESS,
      });
      return;
    }

    // Validating via userValidationSecret.
    const requestBody = {
      userValidationSecret: await getUserValidationSecretFromStorage(
        sessionId,
        answers,
      ),
    };

    // Hard requirement: this flow must validate via userValidationSecret.
    // If it's not available, fail fast (no fallback to answerHashes).
    if (!requestBody.userValidationSecret) {
      throw BayunError.ERROR_REAUTHENTICATION_NEEDED;
    }

    let validateSecurityQuestionsResponse;
    let twoFAClient;
    if (process.env.SOURCE == Constants.SOURCE.BAYUN_ADMIN) {
      twoFAClient = getNonAuthRestClientForAdmin(sessionId);
      validateSecurityQuestionsResponse = await twoFAClient.post(
        "/admin/twofa/validateSecurityQuestions",
        requestBody,
      );
    } else {
      const appId = await getFromStorage(sessionId, SharedPreferences.APP_ID);
      const appSecret = await getFromStorage(
        sessionId,
        SharedPreferences.APP_SECRET,
      );

      // create a rest client
      twoFAClient = getTwoFARestClient(appId, sessionId);
      validateSecurityQuestionsResponse = await twoFAClient.post(
        "/twofa/validateSecurityQuestions",
        requestBody,
      );
    }

    // console.log(validateSecurityQuestionsResponse);
    await logApiResponse(
      "validateSecurityQuestionsResponse",
      validateSecurityQuestionsResponse,
    );

    await saveInStorage(sessionId, SharedPreferences.FIRST_TIME_LOG_IN, validateSecurityQuestionsResponse.data.isfirstComponentRenderd);

    const memberAppId = await getFromStorage(sessionId, SharedPreferences.MEMBER_APP_ID);

    if (memberAppId !== null && memberAppId !== undefined) {
      await getRefreshToken(validateSecurityQuestionsResponse, twoFAClient);
    }

    if (process.env.SOURCE != Constants.SOURCE.BAYUN_ADMIN) {
      // verify and save userId
      if (
        !(await retrieveAndverifyLastSignature(
          sessionId,
          validateSecurityQuestionsResponse.data.userId,
        ))
      ) {
        console.log("userId verification failed.");
        failureCallback(BayunError.ERROR_MEMBER_VERIFICATION_FAILED);
        return;
      } else {
        await saveInStorage(
          sessionId,
          SharedPreferences.USER_ID,
          getMessageFromSignatureString(
            validateSecurityQuestionsResponse.data.userId,
          ),
        );
      }
    }

    // save userPublicKey
    // if (validateSecurityQuestionsResponse.data.userPublicKey) {
    //   await saveInStorage(
    //     sessionId,
    //     SharedPreferences.USER_PUBLIC_KEY,
    //     getMessageFromSignatureString(
    //       validateSecurityQuestionsResponse.data.userPublicKey
    //     )
    //   );
    // }

    // save isDev account
    if (validateSecurityQuestionsResponse.data.isDeveloper) {
      await saveInStorage(
        sessionId,
        SharedPreferences.IS_DEVELOPER,
        validateSecurityQuestionsResponse.data.isDeveloper,
      );
    }

    if (
      process.env.SOURCE == Constants.SOURCE.BAYUN_ADMIN &&
      !validateSecurityQuestionsResponse.data.developerKey_kek
    ) {
      await saveInStorage(sessionId, SharedPreferences.IS_USER, "true");
    }

    // decrypt and get userPrivateKey
    const userPrivateKey = await decryptAndGetUserPrivateKey(
      sessionId,
      answers,
      validateSecurityQuestionsResponse,
    );

    await saveInInternalStorage(
      sessionId,
      SharedPreferences.USER_PRIVATE_KEY,
      userPrivateKey,
    );

    if (process.env.SOURCE == Constants.SOURCE.BAYUN_ADMIN) {
      await saveInStorage(
        sessionId,
        SharedPreferences.USER_PRIVATE_KEY,
        userPrivateKey,
      );

      // decrypt and save developerKey
      if (validateSecurityQuestionsResponse.data.developerKey_kek) {
        // if (
        //   !(await retrieveAndverifyLastSignature(
        //     sessionId,
        //     validateSecurityQuestionsResponse.data.developerKey_kek
        //   ))
        // ) {
        //   console.log("developerKey_kek verification failed.");
        //   throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
        // } else {
        //   await saveInStorage(
        //     sessionId,
        //     SharedPreferences.DEVELOPER_KEY_KEK,
        //     getMessageFromSignatureString(
        //       validateSecurityQuestionsResponse.data.developerKey_kek
        //     )
        //   );
        // }

        const developerKey = await decryptAsymmetric(
          validateSecurityQuestionsResponse.data.developerKey,
          userPrivateKey,
          getMessageFromSignatureString(
            validateSecurityQuestionsResponse.data.developerKey_kek,
          ),
          await getFromStorage(sessionId, SharedPreferences.USER_ID),
          await getEncryptionCurveFromStorage(sessionId),
        );

        if (!developerKey) {
          failureCallback(BayunError.ERROR_DEVELOPER_KEY_VERIFICATION_FAILED);
        } else {
          const developerKeyEncoded =
            typeof developerKey === "string"
              ? developerKey
              : encodeKey(developerKey);
          await saveInStorage(
            sessionId,
            SharedPreferences.DEVELOPER_KEY,
            developerKeyEncoded,
          );
        }
      }

      await saveOrgAuthResponseForSwitching(
        sessionId,
        validateSecurityQuestionsResponse,
        failureCallback,
      );

      successCallback({
        sessionId,
        securityQuestions: null,
        authenticationResponse: AuthenticateResponse.SUCCESS,
      });
      return;
    }

    if (await getFromStorage(sessionId, SharedPreferences.REGISTER_FACE_ID)) {
      // setPasscode(passcode);
      await faceCaptureRegisterAndLogin(
        "false",
        successCallback,
        failureCallback,
      );
    } else {
      if (
        await getFromStorage(
          sessionId,
          SharedPreferences.IS_VALIDATE_2FA_FOR_CREATE_MEMBER_APP,
        )
      ) {
        await createMemberAppForSSO(
          sessionId,
          await getFromStorage(sessionId, SharedPreferences.ORG_NAME),
          await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID),
          userPrivateKey,
          failureCallback,
        );
      } else if (
        await getFromStorage(
          sessionId,
          SharedPreferences.IS_VALIDATE_2FA_FOR_CREATE_MEMBER,
        )
      ) {
        await createMemberData(
          sessionId,
          await getFromStorage(sessionId, SharedPreferences.ORG_NAME),
          await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID),
          null,
          await getFromStorage(sessionId, SharedPreferences.APP_ID),
          await getFromInternalStorage(sessionId, SharedPreferences.APP_SECRET),
          await getFromStorage(sessionId, SharedPreferences.APP_KEY_PAIR_ID),
          await getFromStorage(sessionId, SharedPreferences.GHOST_MEMBER_ID),
          await getFromStorage(
            sessionId,
            SharedPreferences.GHOST_MEMBER_SIGNING_PUBLIC_KEY_WITH_SIGNATURES,
          ),
          await getFromStorage(sessionId, SharedPreferences.ADMIN_PUBLIC_KEY),
          await getFromStorage(
            sessionId,
            SharedPreferences.IS_CREATE_ORG_REQUEST,
          ),
          await getFromStorage(sessionId, SharedPreferences.EMAIL_ADDRESS),
          userPrivateKey,
          authorizeMemberCallback,
          failureCallback,
        );
      } else {
        await getMemberClientData(
          sessionId,
          await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID),
          await getFromStorage(sessionId, SharedPreferences.ORG_NAME),
          // passcode
          null,
          failureCallback,
        );
      }
      // removeLoadMask();
      successCallback({
        sessionId,
        securityQuestions: null,
        authenticationResponse: AuthenticateResponse.SUCCESS,
      });
    }
  } catch (error) {
    // removeLoadMask();
    if (error?.bayunAlreadyHandled) {
      return;
    }
    if (error?.response?.data) {
      failureCallback(getErrorMessage(sessionId, error.response.data));
    } else if (typeof error === "string") {
      failureCallback(error);
    } else {
      failureCallback(error?.message || BayunError.ERROR_SOMETHING_WENT_WRONG);
    }
  }
};

export async function decryptAndGetUserPrivateKey(
  sessionId,
  answers,
  validateSecurityQuestionsResponse,
) {
  // Verify user public key and save it
  if (validateSecurityQuestionsResponse.data.userPublicKey) {
    if (
      !(await retrieveAndverifyLastSignature(
        sessionId,
        validateSecurityQuestionsResponse.data.userPublicKey,
      ))
    ) {
      console.log("UserPublicKey verification failed.");
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    } else {
      await saveInStorage(
        sessionId,
        SharedPreferences.USER_PUBLIC_KEY,
        getMessageFromSignatureString(
          validateSecurityQuestionsResponse.data.userPublicKey,
        ),
      );
    }
  }

  // Verify and save developerKey_kek
  if (
    process.env.SOURCE == Constants.SOURCE.BAYUN_ADMIN &&
    validateSecurityQuestionsResponse.data.developerKey_kek
  ) {
    if (
      !(await retrieveAndverifyLastSignature(
        sessionId,
        validateSecurityQuestionsResponse.data.developerKey_kek,
      ))
    ) {
      console.log("developerKey_kek verification failed.");
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    } else {
      await saveInStorage(
        sessionId,
        SharedPreferences.DEVELOPER_KEY_KEK,
        getMessageFromSignatureString(
          validateSecurityQuestionsResponse.data.developerKey_kek,
        ),
      );
    }
  }

  const minimumCorrectQuestions =
    Number(
      await getFromStorage(
        sessionId,
        SharedPreferences.MINIMUM_CORRECT_QUESTIONS,
      ),
    ) || 3;
  const answerKeySalts = validateSecurityQuestionsResponse.data.answerKeySalts;
  const userPrivateKeyParts =
    validateSecurityQuestionsResponse.data.userPrivateKeyParts;

  await saveInStorage(
    sessionId,
    SharedPreferences.ENCRYPTED_USER_PRIVATE_KEY_PARTS,
    JSON.stringify(userPrivateKeyParts),
  );
  await saveInStorage(
    sessionId,
    SharedPreferences.ANSWER_KEY_SALTS,
    JSON.stringify(answerKeySalts),
  );

  const userPrivateKey = await decryptUserPrivateKey(
    userPrivateKeyParts,
    answerKeySalts,
    answers,
    minimumCorrectQuestions,
    true,
  );

  // Restore the user signing identity alongside the encryption key; a session with
  // only the encryption key can decrypt but cannot sign.
  await restoreUserSigningKeys(
    sessionId,
    validateSecurityQuestionsResponse.data,
    userPrivateKey,
    await getFromStorage(sessionId, SharedPreferences.USER_ID),
  );

  if (
    validateSecurityQuestionsResponse.data.frRegistered == "true" &&
    validateSecurityQuestionsResponse.data.frEnabled == "true" &&
    process.env.INCLUDE_VISHWAM == true
  ) {
    //  "New User Key is generated again when userAccount is recovered,, hence using userKey is not good"
    // Use the locally decrypted key: USER_PRIVATE_KEY is not written to session storage until
    // after this function returns, so reading it from storage here yields undefined on every
    // source (only internal storage is populated, and only later).
    const rawUserKey = asRawKey(
      await decryptAsymmetric(
        validateSecurityQuestionsResponse.data.userKey,
        userPrivateKey,
        validateSecurityQuestionsResponse.data.userKeyKek,
        await getFromStorage(sessionId, SharedPreferences.USER_ID),
        await getEncryptionCurveFromStorage(sessionId),
      ),
    );

    //  "New User Key is generated again when userAccount is recovered,, hence using userKey is not good"
    const frDecryptionKey = encodeKey(
      await hmacSha256Raw(
        rawUserKey,
        await getFromStorage(sessionId, SharedPreferences.USER_ID),
      ),
    );
    await saveInStorage(
      sessionId,
      SharedPreferences.FR_DECRYPTION_KEY,
      frDecryptionKey,
    );
    await saveInStorage(sessionId, SharedPreferences.REGISTER_FACE_ID, true);
    initializeVishwamSDK(sessionId);
  }

  return userPrivateKey;
}

/**
 * Decrypts encrypted parts per answer; successful decrypts are correct shares.
 * Joins the first k shares using shamir's secret sharing.
 *
 * @param {String} encryptedUserPrivateKeyParts encrypted parts to decrypt.
 * @param {String} answerKeySalts salts used to decrypt parts.
 * @param {List<String>} answers list of answers mapped with questionId.
 * @param {Number} k minimum shares required to reconstruct the secret.
 * @param {Boolean} formatAsPrivateKey when true, format as PEM private key.
 */
export async function decryptUserPrivateKey(
  encryptedUserPrivateKeyParts,
  answerKeySalts,
  answers,
  k,
  formatAsPrivateKey = true,
) {
  if (!encryptedUserPrivateKeyParts || !answerKeySalts || !answers?.length) {
    throw BayunError.ERROR_INCORRECT_ANSWERS;
  }

  if (!k || k < 1) {
    throw BayunError.ERROR_INCORRECT_ANSWERS;
  }

  const attempts = answers.map((element) => ({
    questionId: element.questionId,
    answer: element.answer,
  }));

  const decryptedPartsMap = new Map();

  for (const { questionId, answer } of attempts) {
    const keyAnswerSalt = findKeyAnswerSalt(questionId, answerKeySalts);
    const encryptedPart = findEncryptedUserPrivateKeyPart(
      questionId,
      encryptedUserPrivateKeyParts,
    );
    if (!encryptedPart || !keyAnswerSalt) {
      continue;
    }

    const decryptedPart = await aeadDecryptWithPasscode(
      encryptedPart,
      answer,
      keyAnswerSalt,
      questionId,
    );

    if (decryptedPart) {
      const decryptedPartBytes = convertBase64StringToArrayBuffer(decryptedPart);
      decryptedPartsMap.set(questionId, new Uint8Array(decryptedPartBytes));
    }
  }

  if (decryptedPartsMap.size < k) {
    throw BayunError.ERROR_INCORRECT_ANSWERS;
  }

  const shareIds = Array.from(decryptedPartsMap.keys()).slice(0, k);
  const sharesForJoin = Object.fromEntries(
    shareIds.map((id) => [id, decryptedPartsMap.get(id)]),
  );

  let recovered;
  try {
    recovered = joinPrefixedShares(sharesForJoin);
  } catch (e) {
    throw BayunError.ERROR_INCORRECT_ANSWERS;
  }

  const recoveredString = convertArrayBufferToString(recovered);

  if (!formatAsPrivateKey) {
    return recoveredString.trim();
  }

  return formatRecoveredAsPemPrivateKey(recoveredString);
}

/**
 * Reconstructs userValidationSecret from validation-secret shards and answers.
 * Returns null when validationSecretDataRequest is missing or empty.
 */
export async function buildUserValidationSecret(
  validationSecretDataRequest,
  answers,
  minimumCorrectQuestions = 3,
) {
  const mapped = mapValidationSecretDataRequestForReauthentication(
    validationSecretDataRequest,
  );
  if (!mapped) {
    return null;
  }

  const { encryptedParts, keySalts } = mapped;
  return decryptUserPrivateKey(
    encryptedParts,
    keySalts,
    answers,
    minimumCorrectQuestions,
    false,
  );
}

/**
 * Loads validationSecretDataRequest from session storage and builds userValidationSecret.
 */
export async function getUserValidationSecretFromStorage(sessionId, answers) {
  const validationSecretDataRequestStr = await getFromStorage(
    sessionId,
    SharedPreferences.VALIDATION_SECRET_DATA_REQUEST,
  );
  if (!validationSecretDataRequestStr) {
    return null;
  }

  const validationSecretDataRequest = JSON.parse(
    validationSecretDataRequestStr,
  );
  if (!validationSecretDataRequest?.length) {
    return null;
  }

  const minimumCorrectQuestions =
    Number(
      await getFromStorage(
        sessionId,
        SharedPreferences.MINIMUM_CORRECT_QUESTIONS,
      ),
    ) || 3;

  return buildUserValidationSecret(
    validationSecretDataRequest,
    answers,
    minimumCorrectQuestions,
  );
}

export async function decryptUserPrivateKeyForReauthentication(
  sessionId,
  encryptedUserPrivateKeyParts,
  answerKeySalts,
  answers,
  k = 3,
  formatAsPrivateKey = true,
  persist = true,
) {
  console.log("decryptUserPrivateKeyForReauthentication");
  const recovered = await decryptUserPrivateKey(
    encryptedUserPrivateKeyParts,
    answerKeySalts,
    answers,
    k,
    formatAsPrivateKey,
  );

  if (!recovered) {
    throw BayunError.ERROR_INCORRECT_ANSWERS;
  }

  if (persist) {
    await saveInStorage(
      sessionId,
      SharedPreferences.USER_PRIVATE_KEY,
      recovered,
    );
    return;
  }

  return recovered;
}
